import { useEffect, useRef, useState, useCallback } from "react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Activity, Pin, Send, Loader2, Trash2, Star, Skull, Crown, ShieldCheck, Trophy, Sparkles, Leaf } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { HelpRequestButton } from "@/components/HelpRequestButton";

function relativeTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 5) return "just now";
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
}

const typeIconMap = {
    owner_post: { Icon: Star, color: "#facc15" },
    activity: { Icon: Sparkles, color: "#a855f7" },
};
const subtypeIconMap = {
    subscribe: { Icon: Crown, color: "#a855f7" },
    picks_drop: { Icon: Trophy, color: "#22c55e" },
    role: { Icon: ShieldCheck, color: "#22d3ee" },
    win: { Icon: Trophy, color: "#facc15" },
};

const LiveCard = ({ item, canManage, onTogglePin, onDelete }) => {
    const sub = item.subtype && subtypeIconMap[item.subtype];
    const map = sub || typeIconMap[item.kind] || typeIconMap.activity;
    const { Icon, color } = map;
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="pp-bg-1 rounded-xl border border-[#242436] p-4 flex items-start gap-3 group relative"
            style={{ boxShadow: `0 0 0 1px ${color}22, 0 0 18px ${color}1a` }}
            data-testid="live-update-item"
        >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0" style={{ background: `${color}22`, color }}>
                <Icon className="w-4.5 h-4.5" />
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-[#8b8ba0] font-mono uppercase">
                    {item.pinned && <span className="inline-flex items-center gap-1 text-[#facc15]"><Pin className="w-3 h-3" /> PINNED</span>}
                    {item.author_name && (
                        <span>
                            {item.author_role === "admin" ? "Owner" : item.author_role === "support" ? "Support" : ""} {item.author_name}
                        </span>
                    )}
                    <span>· {relativeTime(item.created_at)}</span>
                </div>
                <p className="mt-1 text-white whitespace-pre-wrap break-words">{item.content}</p>
            </div>
            {canManage && (
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => onTogglePin(item)} className="text-xs text-[#facc15] hover:text-yellow-200" title="Pin"><Pin className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDelete(item)} className="text-xs text-[#ef4444] hover:text-red-300" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
            )}
        </motion.div>
    );
};

export default function Live() {
    const { user, loading: authLoading } = useAuth();
    const [pinned, setPinned] = useState([]);
    const [items, setItems] = useState([]);
    const [content, setContent] = useState("");
    const [pinnedNew, setPinnedNew] = useState(false);
    const [posting, setPosting] = useState(false);
    const lastIdsRef = useRef(new Set());

    const canPost = user && (user.role === "admin" || user.role === "support");
    const canManage = canPost;

    const fetchFeed = useCallback(async () => {
        try {
            const res = await api.get("/live");
            setPinned(res.data.pinned || []);
            setItems(res.data.items || []);
            // Track new IDs
            const allIds = new Set([...(res.data.pinned || []), ...(res.data.items || [])].map((i) => i.id));
            lastIdsRef.current = allIds;
        } catch (e) { /* noop */ }
    }, []);

    useEffect(() => {
        fetchFeed();
        const id = setInterval(fetchFeed, 8000);
        return () => clearInterval(id);
    }, [fetchFeed]);

    const post = async (e) => {
        e?.preventDefault();
        const c = content.trim();
        if (!c) return;
        setPosting(true);
        try {
            await api.post("/live", { content: c, pinned: pinnedNew });
            setContent("");
            setPinnedNew(false);
            toast.success("Live update posted");
            await fetchFeed();
        } catch (e) {
            toast.error("Failed to post");
        } finally {
            setPosting(false);
        }
    };

    const togglePin = async (item) => {
        try { await api.post(`/live/${item.id}/pin`); await fetchFeed(); } catch (e) {}
    };
    const deleteItem = async (item) => {
        try { await api.delete(`/live/${item.id}`); toast.success("Deleted"); await fetchFeed(); } catch (e) { toast.error("Failed to delete"); }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-8 sm:py-12">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="mb-6">
                        <p className="text-xs font-mono uppercase text-[#22c55e] tracking-widest">Real-time</p>
                        <h1 className="font-display text-3xl sm:text-4xl tracking-widest inline-flex items-center gap-2"><Activity className="w-6 h-6 text-[#22c55e]" /> LIVE UPDATES</h1>
                        <p className="mt-1 text-sm text-[#8b8ba0]">Pinned owner posts + auto activity feed. Refreshes every few seconds.</p>
                    </div>

                    {authLoading && (
                        <Card className="pp-bg-1 border-[#242436] text-white mb-6">
                            <CardContent className="p-4 text-center text-sm text-[#8b8ba0]">
                                <Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading...
                            </CardContent>
                        </Card>
                    )}
                    {!authLoading && canPost && (
                        <Card className="pp-bg-1 border-[#242436] text-white mb-6" data-testid="live-composer">
                            <CardContent className="p-4">
                                <form onSubmit={post} className="space-y-3">
                                    <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={2} placeholder="Drop a live update for the squad…" className="pp-bg-0 border-[#242436] text-white" data-testid="live-input" />
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="inline-flex items-center gap-2">
                                            <Switch id="pinit" checked={pinnedNew} onCheckedChange={setPinnedNew} />
                                            <Label htmlFor="pinit" className="text-sm text-[#b7b7c6] inline-flex items-center gap-1"><Pin className="w-3.5 h-3.5" /> Pin this update</Label>
                                        </div>
                                        <Button type="submit" disabled={posting || !content.trim()} className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid="live-post-button">
                                            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Send className="w-4 h-4" /> Post</span>}
                                        </Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    {pinned.length > 0 && (
                        <div className="space-y-3 mb-6" data-testid="live-pinned">
                            <p className="text-xs font-mono uppercase text-[#facc15] tracking-widest inline-flex items-center gap-1"><Pin className="w-3 h-3" /> Pinned</p>
                            <AnimatePresence>
                                {pinned.map((p) => (
                                    <LiveCard key={p.id} item={p} canManage={canManage} onTogglePin={togglePin} onDelete={deleteItem} />
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                    <p className="text-xs font-mono uppercase text-[#8b8ba0] tracking-widest mb-3">Activity</p>
                    <div className="space-y-3" data-testid="live-feed">
                        <AnimatePresence>
                            {items.length === 0 && pinned.length === 0 ? (
                                <p className="text-center text-sm text-[#8b8ba0] py-12">No live updates yet.</p>
                            ) : items.map((it) => (
                                <LiveCard key={it.id} item={it} canManage={canManage} onTogglePin={togglePin} onDelete={deleteItem} />
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </section>
            <Footer />
            <HelpRequestButton />
        </div>
    );
}
